import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getGetEnginePlayMysessionQueryKey } from '@/types/origin-point-engine'
import { logger } from '@/lib/logger'
import { useEnginePlayActions, clearMessagesCache } from '@/features/chats'
import type { PreviewSessionType } from '@/features/chats/types/type'
import { useSessions } from '@/features/sessions/hooks/useSessions'
import { usePreviewRailStore } from '../components/preview/preview-rail-store'
import { useEditorStore } from '../stores/editor-store'

/**
 * 开始预览会话的参数 (可选，从 editor store 自动获取)
 */
interface StartPreviewChatOptions {
  /** 会话类型，默认 'draft' */
  sessionType?: PreviewSessionType
}

/**
 * 开始预览会话的返回值
 */
interface StartPreviewChatResult {
  /** 会话ID */
  sessionId: string
}

/**
 * usePreviewAction - 编辑器预览业务逻辑
 *
 * 职责：
 * 1. 从 editor store 获取当前编辑的引擎 ID
 * 2. 查找现有的预览 session 或创建新的预览会话
 * 3. 管理 preview-rail-store 状态
 *
 * 注意：engine 数据由 useEngine hook 在 Chat 页面自动获取并同步到 store
 */
export const usePreviewAction = () => {
  const queryClient = useQueryClient()
  const { sessionList, endSessionAsync } = useSessions()
  const { startPreview } = useEnginePlayActions()
  const { setPreviewSession, setRestarting } = usePreviewRailStore()

  // 从 editor store 获取当前引擎 ID
  const engineId = useEditorStore((s) => s.draft.engineId)

  /**
   * 开始预览 Chat
   *
   * 逻辑：
   * 1. 从 editor store 获取引擎 ID
   * 2. 在 sessionList 中查找 engineId 匹配的 session
   * 3. 如果找到 → 使用现有会话
   * 4. 如果没找到 → 调用 startPreview 创建新预览会话
   */
  const startPreviewChat = useCallback(
    async (
      options: StartPreviewChatOptions = {}
    ): Promise<StartPreviewChatResult | null> => {
      const { sessionType = 'draft' } = options

      if (!engineId) {
        logger.warn('[startPreviewChat] 无有效的 engineId')
        return null
      }

      logger.debug('[startPreviewChat] 开始查找现有会话', { engineId })

      // 2. 在 sessionList 中查找匹配的 session
      const existingSession = sessionList?.sessions?.find(
        (session) => session.engineId === engineId
      )

      if (existingSession?.id) {
        // 3. 找到现有会话
        logger.debug('[startPreviewChat] 找到现有会话', {
          sessionId: existingSession.id,
        })

        // 使该会话的消息列表缓存失效，确保获取最新数据
        clearMessagesCache(queryClient, existingSession.id)

        // 更新 preview store
        setPreviewSession(existingSession.id)

        return {
          sessionId: existingSession.id,
        }
      }

      // 4. 没找到 → 调用 startPreview 创建新会话
      logger.debug('[startPreviewChat] 未找到现有会话，创建新预览')

      const result = await startPreview({
        engineId,
        sessionType,
      })

      if (result?.sessionId) {
        setPreviewSession(result.sessionId)

        return {
          sessionId: result.sessionId,
        }
      }

      logger.error('[startPreviewChat] 创建预览失败')
      return null
    },
    [engineId, sessionList, startPreview, setPreviewSession, queryClient]
  )

  /**
   * 重启预览 Chat
   *
   * 逻辑：
   * 1. 查找现有的与 engineId 匹配的会话
   * 2. 如果找到，删除该会话
   * 3. 创建新的预览会话
   * 4. 刷新会话列表缓存
   */
  const restartPreviewChat = useCallback(
    async (
      options: StartPreviewChatOptions = {}
    ): Promise<StartPreviewChatResult | null> => {
      try {
        // 1. 设置加载状态
        setRestarting(true)

        if (!engineId) {
          setRestarting(false)
          logger.warn('[restartPreviewChat] 无有效的引擎 ID')
          return null
        }

        logger.debug('[restartPreviewChat] 开始重启预览', { engineId })

        // 3. 查找现有会话
        const existingSession = sessionList?.sessions?.find(
          (session) => session.engineId === engineId
        )

        // 4. 如果存在，删除现有会话
        if (existingSession?.id) {
          logger.debug('[restartPreviewChat] 找到现有会话，准备删除', {
            sessionId: existingSession.id,
          })

          await endSessionAsync({ data: { sessionId: existingSession.id } })
          logger.debug('[restartPreviewChat] 已删除现有会话')
        }

        // 5. 创建新预览会话
        const { sessionType = 'draft' } = options
        const result = await startPreview({
          engineId,
          sessionType,
        })

        if (result?.sessionId) {
          // 更新 preview store
          setPreviewSession(result.sessionId)

          // 6. 刷新会话列表缓存
          queryClient.invalidateQueries({
            queryKey: getGetEnginePlayMysessionQueryKey(),
          })
          logger.debug('[restartPreviewChat] 会话列表缓存已刷新')

          setRestarting(false)
          return {
            sessionId: result.sessionId,
          }
        }

        logger.error('[restartPreviewChat] 创建新预览会话失败')
        setRestarting(false)
        return null
      } catch (error) {
        logger.error('[restartPreviewChat] 重启预览失败', error)
        setRestarting(false)
        return null
      }
    },
    [
      engineId,
      sessionList,
      endSessionAsync,
      startPreview,
      setPreviewSession,
      setRestarting,
      queryClient,
    ]
  )

  return {
    startPreviewChat,
    restartPreviewChat,
  }
}
